import {
    DEFAULT_EMAIL_BODY_AFTER_LINK,
    DEFAULT_POST_INTAKE_SUBJECT,
    INTAKE_EMAIL_BODY_DEFAULTS,
    INTAKE_REMINDER_EMAIL_BODY_DEFAULTS,
} from '@common/constants';
import type { ProspectMatterType, ProspectPracticeArea } from '@common/enums';
import { getIsIntakeFormV2 } from '@common/utils/intakeFormVersion';
import { Box, Button, Stack, Step, StepLabel, Stepper, Tooltip, useTheme } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import LoadingButton from '@/components/Buttons/LoadingButton';
import FullScreenLoader from '@/components/FullScreenLoader';
import type { UploadedDocument } from '@/components/NewProspectModal/RetainerAgreement/DocumentSection';
import { toTemplateDocument } from '@/components/NewProspectModal/RetainerAgreement/templateDocuments';
import { DEFAULT_INTAKE_FORM_DESCRIPTION, DEFAULT_INTAKE_TEMPLATE_FOOTER } from '@/constants';
import { useLawFirmContext } from '@/hooks/useLawFirmContext';
import { useOnMount } from '@/hooks/useOnMount';
import { useLawFirm } from '@/queries/lawFirms';
import { quoteTemplateAttachmentsQueryOptions } from '@/queries/templates';
import { useUsers } from '@/queries/users';
import {
    getIntakeEmail,
    getIntakeReminderEmail,
    getPostIntakeEmail,
} from '@/services/EmailService';
import { fetchQuoteTemplateById } from '@/services/QuoteTemplatesService';
import { getIntakeTemplateForms } from '@/services/TemplateFormsService';
import { reportFormGroupMismatches } from '@/utils/formGroupMonitoring';
import IntakeDocumentsSetup from './IntakeDocumentsSetup';
import IntakeForm from './IntakeForm';
import IntakePostIntakeEmailSetup from './IntakePostIntakeEmailsSetup';
import IntakeReminderEmailSetup from './IntakeReminderEmailSetup';
import IOMatterType from './IOMatterType';
import type {
    IntakeDesignData,
    IntakeEmailData,
    IntakeReminderEmailData,
    MatterTypeData,
    StepRef,
} from './types';

// URL segment for each step, in order
const INTAKE_ONLY_STEPS = ['matter-type', 'intake-design', 'intake-email', 'documents'];

function getStepPath(step: number, quoteTemplateId: number | null) {
    const templateId = quoteTemplateId ? `/${quoteTemplateId}` : '';
    return `/intake-only/setup/${INTAKE_ONLY_STEPS[step]}${templateId}`;
}

export default function IntakeOnlySetup() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { step, quoteTemplateId: quoteTemplateIdParam } = useParams();
    const theme = useTheme();
    const { activeLawFirmId } = useLawFirmContext();

    // Law firm ID from the fetched quote template (used when editing an existing template)
    const [templateLawFirmId, setTemplateLawFirmId] = useState<number | undefined>();

    // Prefer the template's firm when editing; otherwise use the active firm
    // (SideNav selection for Super Admins, own firm for everyone else).
    const selectedLawFirmId = templateLawFirmId ?? activeLawFirmId;

    // The route remounts this page when the template ID in the URL changes, such as after creating one
    const quoteTemplateId = Number(quoteTemplateIdParam) || null;

    const [isFullScreenLoading, setIsFullScreenLoading] = useState(false);

    // Step Management. The URL holds the step, and only matter type is available until the
    // template exists
    const urlStep = INTAKE_ONLY_STEPS.indexOf(step ?? '');
    const currentStep = quoteTemplateId && urlStep !== -1 ? urlStep : 0;
    const goToStep = (nextStep: number) => navigate(getStepPath(nextStep, quoteTemplateId));
    const [isScreenTwo, setIsScreenTwo] = useState(false);
    const [isNextStepLoading, setIsNextStepLoading] = useState(false);
    const matterTypeRef = useRef<StepRef>(null);
    const intakeFormRef = useRef<StepRef>(null);
    const intakeEmailRef = useRef<StepRef>(null);
    const reminderEmailRef = useRef<StepRef>(null);
    const documentsRef = useRef<StepRef>(null);

    // Step 0 (Matter Type)
    const [matterType, setMatterType] = useState<MatterTypeData>({
        practiceArea: null,
        showError: undefined,
        transactionType: null,
        templateName: '',
        assignedLawyer: 0,
        emailCC: [],
        isPostIntakeEnable: true,
    });

    // Fetch users from the template's law firm
    const { data: users = [] } = useUsers({
        enabled: !!selectedLawFirmId,
        lawFirmId: selectedLawFirmId,
    });

    const { data: selectedLawFirm } = useLawFirm(selectedLawFirmId);

    const lawyerSignature =
        users.find((user) => user.userId === matterType.assignedLawyer)?.signature ?? '';

    // Step 1 (Intake Design)
    const [intakeDesign, setIntakeDesign] = useState<IntakeDesignData>({
        intakeFormDescription: DEFAULT_INTAKE_FORM_DESCRIPTION,
        textAfterSubmitIntakeForClient: DEFAULT_INTAKE_TEMPLATE_FOOTER,
        quoteTemplateId: undefined,
        templateFormId: undefined,
        templateFormFields: [],
        metadata: undefined,
        currentIntakeTab: {
            tab: 0,
            tabLabel: '',
        },
    });

    // Step 2 - Screen 1 (Intake + Post Intake Emails)
    const [intakeEmail, setIntakeEmail] = useState<IntakeEmailData>({
        intakeEmailTemplateId: null,
        subject: '',
        bodyBeforeLink: '',
        bodyAfterLink: DEFAULT_EMAIL_BODY_AFTER_LINK,
        isQuoteApprovalOn: false,
        expiry: 180,
        postIntakeSubject: DEFAULT_POST_INTAKE_SUBJECT,
    });

    // Step 2 - Screen 2 (Intake Reminder Email)
    const [intakeReminderEmail, setIntakeReminderEmail] = useState<IntakeReminderEmailData>({
        intakeReminderEmailTemplateId: null,
        isQuoteReminderOn: true,
        reminderSubject: '',
        reminderBodyBeforeLink: '',
        reminderBodyAfterLink: DEFAULT_EMAIL_BODY_AFTER_LINK,
        enableReminderExhaustNotification: true,
        emailFrequencyOfReminder: 'EveryThirdDay',
        reminderTime: 'Morning(09:00 AM)',
        totalNumberOfReminders: 3,
    });

    // Step 3 (Documents). Edits start from the saved attachments
    const attachmentsQuery = useQuery(quoteTemplateAttachmentsQueryOptions(quoteTemplateId));
    const [editedAttachments, setEditedAttachments] = useState<UploadedDocument[]>();
    const attachments = editedAttachments ?? attachmentsQuery.data?.map(toTemplateDocument);
    const [defaultRetainerId, setDefaultRetainerId] = useState<number | null>(null);

    // Loads the saved template, including the email defaults for its matter type. A new template
    // gets these on the remount after it's created
    const fetchData = async () => {
        if (!quoteTemplateId) return;

        setIsFullScreenLoading(true);
        try {
            const [
                templateResp,
                intakeDesignResp,
                intakeEmailResp,
                postIntakeEmailResp,
                reminderEmailResp,
            ] = await Promise.allSettled([
                fetchQuoteTemplateById(quoteTemplateId),
                getIntakeTemplateForms(quoteTemplateId),
                getIntakeEmail(quoteTemplateId),
                getPostIntakeEmail(quoteTemplateId),
                getIntakeReminderEmail(quoteTemplateId),
            ]);
            if (templateResp.status === 'fulfilled') {
                const templateData = templateResp.value.data;

                // Store the template's law firm ID so edit flows stay scoped
                // to the template's firm even if the SideNav selection differs.
                if (templateData.lawFirmId) {
                    setTemplateLawFirmId(templateData.lawFirmId);
                }

                setMatterType((prev) => ({
                    ...prev,
                    transactionType: templateData.process as ProspectMatterType,
                    templateName: templateData.quoteTemplateName,
                    assignedLawyer: templateData.quoteApproverId,
                    emailCC: templateData.emailCC ?? [],
                    practiceArea: templateData.practiceArea as ProspectPracticeArea,
                    isPostIntakeEnable: templateData.isPostIntakeEnable,
                }));
                setDefaultRetainerId(templateData.defaultRetainerAgreementId);

                // Set email subjects and introductions based on selected matter type
                // (Will be overwritten below if intake or reminder steps have been completed)
                const intakeDefaults =
                    INTAKE_EMAIL_BODY_DEFAULTS[templateData.process as ProspectMatterType];
                setIntakeEmail((prev) => ({
                    ...prev,
                    subject: intakeDefaults?.subject ?? '',
                    bodyBeforeLink: intakeDefaults?.bodyBeforeLink ?? '',
                    bodyAfterLink: intakeDefaults?.bodyAfterLink ?? '',
                }));
                const intakeReminderDefaults =
                    INTAKE_REMINDER_EMAIL_BODY_DEFAULTS[templateData.process as ProspectMatterType];
                setIntakeReminderEmail((prev) => ({
                    ...prev,
                    reminderSubject: intakeReminderDefaults?.subject ?? '',
                    reminderBodyBeforeLink: intakeReminderDefaults?.bodyBeforeLink ?? '',
                    reminderBodyAfterLink: intakeReminderDefaults?.bodyAfterLink ?? '',
                }));
            }
            if (intakeDesignResp.status === 'fulfilled') {
                const intakeData = intakeDesignResp.value.data;
                if (getIsIntakeFormV2(intakeData.practiceArea, intakeData.process)) {
                    reportFormGroupMismatches(
                        'builder',
                        intakeData.templateFormId,
                        intakeData.formGroups,
                        intakeData.templateFormFields,
                    );
                }
                setIntakeDesign((prev) => ({
                    ...prev,
                    intakeFormDescription: intakeData.headerDescription ?? '',
                    textAfterSubmitIntakeForClient: intakeData.footerTemplate ?? '',
                    quoteTemplateId: intakeData.quoteTemplateId,
                    templateFormId: intakeData.templateFormId,
                    templateFormFields: intakeData.templateFormFields,
                    formGroups: intakeData.formGroups ?? undefined,
                    metadata: intakeData.metadata,
                }));
            }
            if (intakeEmailResp.status === 'fulfilled') {
                setIntakeEmail((prev) => ({
                    ...prev,
                    ...intakeEmailResp.value,
                }));
            }
            if (postIntakeEmailResp.status === 'fulfilled') {
                // Keep the default subject if none was saved
                setIntakeEmail((prev) => ({
                    ...prev,
                    postIntakeSubject:
                        postIntakeEmailResp.value.postIntakeSubject ?? prev.postIntakeSubject,
                }));
            }
            if (reminderEmailResp.status === 'fulfilled') {
                setIntakeReminderEmail(reminderEmailResp.value);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsFullScreenLoading(false);
        }
    };

    useOnMount(fetchData);

    const handleNext = async () => {
        setIsNextStepLoading(true);
        if (currentStep === 0) {
            const result = await matterTypeRef.current?.submit();
            if (result?.success) {
                // Saved template details are cached under ['templates', quoteTemplateId]
                await queryClient.invalidateQueries({ queryKey: ['templates'] });
                // A new template has already moved on to its own URL in handleCreate
                if (quoteTemplateId) {
                    goToStep(1);
                }
            }
        } else if (currentStep === 1) {
            const result = await intakeFormRef.current?.submit();
            if (result?.success) {
                goToStep(2);
            }
        } else if (currentStep === 2 && !isScreenTwo) {
            const result = await intakeEmailRef.current?.submit();
            if (result?.success) {
                await queryClient.invalidateQueries({ queryKey: ['templates'] });
                setIsScreenTwo(true);
            }
        } else if (currentStep === 2 && isScreenTwo) {
            const result = await reminderEmailRef.current?.submit();
            if (result?.success) {
                await queryClient.invalidateQueries({ queryKey: ['templates'] });
                goToStep(3);
            }
        } else if (currentStep === 3) {
            const result = await documentsRef.current?.submit();
            if (result?.success) {
                await queryClient.invalidateQueries({ queryKey: ['templates'] });
                navigate('/templates/intakes/');
            }
        }
        setIsNextStepLoading(false);
    };

    const handleBack = () => {
        if (currentStep === 2 && isScreenTwo) {
            setIsScreenTwo(false);
        } else {
            // From Documents, isScreenTwo is still set, so this returns to the reminder email
            goToStep(currentStep - 1);
        }
    };

    // The new URL remounts the page, which loads the new template
    const handleCreate = (createdQuoteTemplateId: number) => {
        navigate(getStepPath(1, createdQuoteTemplateId));
    };

    // Unknown steps, or later steps before the template exists, go to the step being shown
    if (step !== INTAKE_ONLY_STEPS[currentStep]) {
        return <Navigate to={getStepPath(currentStep, quoteTemplateId)} replace />;
    }

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateRows: '96px 1fr 72px',
                height: '100vh',
                overflow: 'hidden',
            }}
        >
            {isFullScreenLoading && <FullScreenLoader />}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr',
                    alignItems: 'center',
                    height: '100%',
                    width: '100%',
                    boxSizing: 'border-box',
                    backgroundColor: '#FFFFFF',
                    boxShadow: theme.customShadows.modal,
                    borderBottom: `1px solid ${theme.palette.neutral.borderColor}`,
                }}
            >
                <Stepper activeStep={currentStep} alternativeLabel>
                    <Step>
                        <StepLabel>Matter type</StepLabel>
                    </Step>
                    <Step>
                        <StepLabel>Intake design</StepLabel>
                    </Step>
                    <Step>
                        <StepLabel>Intake email</StepLabel>
                    </Step>
                    <Step>
                        <StepLabel>Documents</StepLabel>
                    </Step>
                </Stepper>
            </Box>
            {currentStep === 0 && (
                <IOMatterType
                    ref={matterTypeRef}
                    quoteTemplateId={quoteTemplateId}
                    users={users}
                    matterType={matterType}
                    setMatterType={setMatterType}
                    selectedLawFirmId={selectedLawFirmId}
                    isTestFirm={selectedLawFirm?.isTestFirm ?? false}
                    onCreate={(id) => handleCreate(id)}
                />
            )}
            {currentStep === 1 && (
                <IntakeForm
                    ref={intakeFormRef}
                    quoteTemplateId={quoteTemplateId}
                    intakeDesign={intakeDesign}
                    setIntakeDesign={setIntakeDesign}
                    matterType={matterType}
                    lawFirmLogoUrl={selectedLawFirm?.logoUrl}
                    currentLawFirmId={selectedLawFirmId}
                />
            )}
            {/* Intake and Post Intake Emails Setup */}
            {currentStep === 2 && !isScreenTwo && (
                <IntakePostIntakeEmailSetup
                    ref={intakeEmailRef}
                    quoteTemplateId={quoteTemplateId}
                    setIsPostIntakeEnable={(value) => {
                        setMatterType((prev) => ({
                            ...prev,
                            isPostIntakeEnable: value,
                        }));
                    }}
                    intakeEmail={intakeEmail}
                    setIntakeEmail={setIntakeEmail}
                    matterType={matterType}
                    intakeDesign={intakeDesign}
                    lawyerSignature={lawyerSignature}
                />
            )}
            {/* Intake Reminder Email Setup */}
            {currentStep === 2 && isScreenTwo && (
                <IntakeReminderEmailSetup
                    ref={reminderEmailRef}
                    quoteTemplateId={quoteTemplateId}
                    intakeReminder={intakeReminderEmail}
                    setIntakeReminder={setIntakeReminderEmail}
                    matterType={matterType}
                    intakeDesign={intakeDesign}
                    lawyerSignature={lawyerSignature}
                />
            )}
            {currentStep === 3 && (
                <IntakeDocumentsSetup
                    ref={documentsRef}
                    quoteTemplateId={quoteTemplateId}
                    lawFirmId={selectedLawFirmId}
                    users={users}
                    practiceArea={matterType.practiceArea}
                    matterType={matterType.transactionType}
                    defaultRetainerId={defaultRetainerId}
                    onDefaultRetainerChange={setDefaultRetainerId}
                    attachments={attachments}
                    onAttachmentsChange={setEditedAttachments}
                    isLoadError={attachmentsQuery.isError}
                    onRetryLoad={() => void attachmentsQuery.refetch()}
                />
            )}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    backgroundColor: '#FFFFFF',
                    justifyContent: 'flex-end',
                    boxSizing: 'border-box',
                    padding: '0 32px',
                    gap: '20px',
                    borderTop: `1px solid ${theme.palette.neutral.borderColor}`,
                }}
            >
                <Stack direction="row" spacing={1}>
                    {currentStep > 0 && (
                        <Button variant="outlined" onClick={handleBack}>
                            Back
                        </Button>
                    )}
                    <Tooltip
                        placement="top"
                        title={
                            matterType.transactionType === null
                                ? 'Please select transaction type'
                                : null
                        }
                    >
                        <LoadingButton
                            disabled={
                                matterType.transactionType === null ||
                                matterType.assignedLawyer === 0 ||
                                matterType.assignedLawyer === undefined ||
                                matterType.templateName === ''
                            }
                            variant="contained"
                            loading={isNextStepLoading}
                            onClick={handleNext}
                        >
                            {currentStep === 3 ? 'Finish' : 'Next'}
                        </LoadingButton>
                    </Tooltip>
                </Stack>
            </Box>
        </Box>
    );
}
